import { FC, ReactNode } from 'react';
import MuiLoadingButton from '@mui/lab/LoadingButton';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import Icon from '../icon';
import { Theme } from '@mui/material';
import { IconType } from '../icon/helpers';

type ButtonType = 'ok' | 'submit' | 'cancel' | 'delete' | 'option';

type ButtonStyleProps = {
  color: 'secondary' | 'primary' | 'inherit' | 'error' | 'success';
  variant: 'contained' | 'outlined';
  inputType: 'button' | 'submit';
};

export interface ButtonProps {
  label?: ReactNode;
  disabled?: boolean;
  size?: 'large' | 'medium' | 'small';
  type?: ButtonType;
  fullWidth?: boolean;
  className?: string;
  icon?: IconType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (event: any) => void; // @todo event type?
  loading?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  button: {
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
    '&.MuiButton-sizeSmall': {
      minHeight: 40,
    },
    '&.cancel:not(.Mui-disabled)': {
      color: theme.palette.darkGray,
      '&:hover': {
        color: theme.palette.darkGray,
      },
    },
  },
  icon: {
    marginRight: 12,
  },
}));

const useStyleProps = (type: ButtonType): ButtonStyleProps => {
  switch (type) {
    case 'ok':
    default:
      return { color: 'primary', variant: 'contained', inputType: 'button' };
    case 'option':
      return { color: 'secondary', variant: 'outlined', inputType: 'button' };
    case 'cancel':
      return { color: 'inherit', variant: 'contained', inputType: 'button' };
    case 'delete':
      return { color: 'error', variant: 'contained', inputType: 'button' };
    case 'submit':
      return { color: 'success', variant: 'contained', inputType: 'button' };
  }
};

const Button: FC<ButtonProps> = ({
  label,
  disabled = false,
  size = 'large',
  icon,
  fullWidth = false,
  className = '',
  onClick,
  type = 'ok',
  loading = false,
}) => {
  const styles = useStyles();
  const { color, variant, inputType } = useStyleProps(type);
  const compClass = classNames(className, type, styles.button);

  const hasTextLabel = !!label;
  const hasIcon = !!icon;

  return (
    <MuiLoadingButton
      color={color}
      className={compClass}
      disabled={disabled}
      size={size}
      variant={variant}
      type={inputType}
      fullWidth={fullWidth}
      onClick={onClick}
      loading={loading}
    >
      {hasIcon && <Icon name={icon} className={hasTextLabel ? styles.icon : ''} size={size} />}
      {label}
    </MuiLoadingButton>
  );
};

export default Button;
